import React, { PureComponent, createRef } from 'react';
import { Document } from 'react-pdf';
import { VariableSizeList } from 'react-window';
import { debounce } from 'throttle-debounce';
import styled from 'styled-components';
import AdavancedLoader from '../AdvancedLoader';
import PageRenderer from './PageRenderer';
import Buttons from './Buttons';
const Styles = styled.div `
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;
class Viewer extends PureComponent {
    constructor(props) {
        super(props);
        this.getContainerWidth = () => {
            const { current } = this._wrapperRef;
            if (!current) {
                return;
            }
            return current.clientWidth;
        };
        this.getContainerHeight = () => {
            const { current } = this._wrapperRef;
            if (!current) {
                return;
            }
            return current.clientHeight;
        };
        this.getScale = (baseWidth) => {
            const { responsiveScale } = this.state;
            const documnetWidth = this.getContainerWidth();
            const scale = (documnetWidth / baseWidth) * responsiveScale;
            return Math.round(scale * 100) / 100;
        };
        this.cachePageDimensions = (pdf) => {
            const promises = Array.from({ length: pdf.numPages }, (v, i) => i + 1).map((pageNumber) => pdf.getPage(pageNumber));
            Promise.all(promises).then((pages) => {
                if (!this._mounted) {
                    return;
                }
                const documnetWidth = this.getContainerWidth();
                const documentHeight = this.getContainerHeight();
                const pageDimensions = new Map();
                for (const page of pages) {
                    const baseWidth = page.view[2];
                    const baseHeight = page.view[3];
                    const scale = this.getScale(baseWidth);
                    const h = baseHeight * scale;
                    pageDimensions.set(page.pageIndex + 1, [
                        documnetWidth,
                        h,
                        scale,
                        baseWidth,
                        baseHeight,
                    ]);
                }
                this.setState({
                    basePageDimensions: { w: 0, h: 0 },
                    cachedPageDimensions: pageDimensions,
                    initialContainerHeight: documentHeight,
                    containerHeight: documentHeight,
                });
            });
        };
        this.recomputeRowHeights = () => {
            const { current } = this._list;
            if (current) {
                current.resetAfterIndex(0);
            }
        };
        this.updatePageHeight = (index) => {
            const { cachedPageDimensions } = this.state;
            if (!cachedPageDimensions) {
                return 0;
            }
            const pageDetails = cachedPageDimensions.get(index + 1);
            const baseWidth = pageDetails[3];
            const baseHeight = pageDetails[4];
            const scale = this.getScale(baseWidth);
            const h = baseHeight * scale;
            return h;
        };
        this.onDocumentLoadSuccess = (pdf) => {
            this.setState({ pdf });
            this.cachePageDimensions(pdf);
        };
        this.updateCurrentVisiblePage = ({ visibleStopIndex }) => {
            this.setState({ currentPage: visibleStopIndex + 1 });
        };
        this.getFixedHeightForPageContainer = () => {
            const { cachedPageDimensions, currentPage } = this.state;
            const item = cachedPageDimensions.get(currentPage);
            const scale = this.getScale(item[3]);
            const currentPageHeight = item[4] * scale;
            const documentHeight = this.getContainerHeight();
            return currentPageHeight < documentHeight
                ? currentPageHeight - 10
                : documentHeight;
        };
        this.handleResize = () => {
            const containerWidth = this.getContainerWidth();
            const containerHeight = this.getFixedHeightForPageContainer();
            this.setState({
                fakeUpdate: {},
                containerWidth,
                containerHeight,
            }, () => this.recomputeRowHeights());
        };
        this.handleScrollClick = (pageNumber) => {
            const { cachedPageDimensions } = this.state;
            const { current } = this._list;
            if (cachedPageDimensions && current) {
                current.scrollToItem(pageNumber - 1, 'start');
            }
        };
        this.handleScaleClick = (type) => {
            const { responsiveScale } = this.state;
            const newScale = type === 'PLUS' ? responsiveScale * 1.15 : responsiveScale * 0.85;
            this.setState({
                responsiveScale: newScale > 1 ? newScale : 1,
            });
        };
        this.handleGetScale = (pageNumber) => {
            const { cachedPageDimensions } = this.state;
            const documentScale = this.getScale(cachedPageDimensions.get(pageNumber)[3]);
            return documentScale;
        };
        this.state = {
            basePageDimensions: null,
            initialContainerHeight: null,
            containerHeight: null,
            containerWidth: null,
            pdf: null,
            currentPage: 1,
            cachedPageDimensions: null,
            responsiveScale: 1,
            pageNumbers: new Map(),
            pages: new WeakMap(),
        };
        this._list = createRef();
        this._wrapperRef = React.createRef();
        this._callResizeHandler = debounce(500, this.handleResize);
        this._callOrientationChangeHandler = debounce(1000, this.handleResize);
    }
    componentDidMount() {
        this._mounted = true;
        window.addEventListener('resize', this._callResizeHandler);
        window.addEventListener('orientationchange', this._callOrientationChangeHandler);
    }
    componentWillUnmount() {
        this._mounted = false;
        window.removeEventListener('resize', this._callResizeHandler);
        window.removeEventListener('orientationchange', this._callOrientationChangeHandler);
    }
    render() {
        const { cachedPageDimensions, containerHeight, containerWidth, pdf, pages, pageNumbers, currentPage, responsiveScale, } = this.state;
        const { dtbs, translations, completed, isEnterprise } = this.props;
        return (React.createElement(Styles, { ref: this._wrapperRef, containerHeight: containerHeight },
            React.createElement(Document, { file: dtbs.url, loading: React.createElement(AdavancedLoader, { text: translations.loadingText }), onLoadSuccess: this.onDocumentLoadSuccess, onLoadError: (error) => console.error(error) }, cachedPageDimensions && (React.createElement(VariableSizeList, { width: containerWidth, height: containerHeight, itemCount: pdf.numPages, itemSize: this.updatePageHeight, itemData: {
                    handleScale: this.handleGetScale,
                    pages,
                    pageNumbers,
                    triggerResize: () => {
                        this.handleResize();
                    },
                }, overscanCount: 1, onItemsRendered: this.updateCurrentVisiblePage, ref: this._list }, PageRenderer))),
            cachedPageDimensions && (React.createElement(Buttons, { numPages: pdf.numPages, handleScrollClick: this.handleScrollClick, handleScaleClick: this.handleScaleClick, currentPage: currentPage, responsiveScale: responsiveScale, downloadUrl: dtbs.url, completed: completed, isEnterprise: isEnterprise }))));
    }
}
export default Viewer;
